// React, Next
import { useTranslation } from 'next-i18next'
import { useContext, useRef } from 'react'
import { StaticImport } from 'next/dist/shared/lib/get-img-props'
import Image from 'next/image'
import { useRouter } from 'next/router'
import Link from 'next/link'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Custom
import { GlobalContext } from 'context/GlobalContextProvider'
import Section from '@/components/sections/Section'

// Img
import SmartVent from 'public/img/solutions/smart_ventilation.webp'
import AirQualityMonitoring from 'public/img/products/Air1000/air-1000.webp'

// Config
import { translateHref } from '@/assets/functions/links'
import i18nextConfig from 'next-i18next.config'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import classes from './OurSolutionsSection-CSS'
import { getFontBody, getFontH2, getFontH4 } from '@/assets/theme/fonts'

export default function OurSolutionsSection() {
  const router = useRouter()
  const currentLocale = router.query.locale || i18nextConfig.i18n.defaultLocale
  const { t } = useTranslation('solutions')
  const { screenDimensions } = useContext(GlobalContext)

  const ref: any = useRef(null)

  const getGrid = () => {
    const w = screenDimensions.width
    if (w > breakpoints.desktopMid) return 'repeat(3, 1fr)'
    if (w > breakpoints.desktopToMobile) return 'repeat(2, 1fr)'
    return 'repeat(1, 1fr)'
  }

  const getImageHeight = () => {
    try {
      return ref.current.getBoundingClientRect().width / 1.5
    } catch {
      return 250
    }
  }

  const getWelcomeSection = () => (
    <Box>
      <Typography
        variant="h1"
        fontSize={getFontH2()}
        sx={{
          color: themeColors.gray.mid,
          textAlign:
            screenDimensions.width < breakpoints.mobileSmall
              ? 'center'
              : 'left',
          fontWeight: 400,
          marginBottom: '2.5rem'
        }}
      >
        {t('solutions.title')}
      </Typography>
      <Typography
        variant="body1"
        fontSize={getFontBody()}
        sx={{
          color: themeColors.gray.mid,
          textAlign:
            screenDimensions.width < breakpoints.mobileSmall
              ? 'center'
              : 'left',
          fontWeight: 400,
          marginBottom: '5rem'
        }}
      >
        {t('solutions.description')}
      </Typography>
    </Box>
  )

  // *** Section ***
  const getCard = (p: {
    id: number
    title: string
    description: string
    img: string | StaticImport
    alt: string
    href: string
  }) => (
    <Box sx={classes.cardBox}>
      <Link
        href={p.href}
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%',
          textDecoration: 'none'
        }}
      >
        <Box>
          <Typography
            variant="h2"
            fontSize={getFontH4()}
            style={classes.typeH4}
            marginBottom={'1rem'}
            sx={{ color: themeColors.gray.mid }}
          >
            {p.title}
          </Typography>
          <Typography
            variant="body1"
            fontSize={getFontBody()}
            style={classes.typeH4b}
            sx={{ color: themeColors.gray.mid }}
          >
            {p.description}
          </Typography>
        </Box>
        <Image
          alt={p.alt}
          src={p.img}
          style={{
            width: 'auto',
            height: getImageHeight(),
            objectFit: 'cover',
            borderRadius: '10px',
            marginTop: '10px',
            alignItems: 'bottom'
          }}
          priority={false}
        />
      </Link>
    </Box>
  )

  const getSection = () => (
    <Box gridTemplateColumns={getGrid()} sx={classes.gridBox}>
      {/*getCard({
        id: 1,
        title: t('solutions.list.card1.name'),
        description: t('solutions.list.card1.description'),
        alt: 'smartVentilation',
        img: SmartVent,
        href: translateHref(currentLocale, 'smart-ventilation')
      })*/}
      {getCard({
        id: 1,
        title: t('solutions.list.card4.name'),
        description: t('solutions.list.card4.description'),
        alt: 'iotAirQualityMonitoring',
        img: AirQualityMonitoring,
        href: translateHref(currentLocale, 'iot-air-quality-monitoring')
      })}
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <>
      <Section justifyContent="left" content={getWelcomeSection()} />
      <Section marginBottom="5rem" content={getSection()} />
    </>
  )
}
